/**
 * @Description 单个Table
 *
 *  按照前端配置isYearData
 *  分为区分年份的表格（单行内展示第一年、第二年数据）
 *  不区分年份的表格（单行内展示两个字段）
 *
 *  被使用处
 *   -基诺浦盈详情组件
 *   -机会点详情核心信息-测算模块
 *   -机会点pdf-基诺浦盈详情组件
 *   -机会点pdf-机会点详情核心信息-测算模块
 */

import { FC, useEffect, useState } from 'react';
import cs from 'classnames';
import styles from './index.module.less';
import pdfStyles from './pdf.module.less';
import V2Table from '@/common/components/Data/V2Table';
import V2Title from '@/common/components/Feedback/V2Title';
import YearDataInput from './YearDataInput';
import SpecialInput from './SpecialInput';
import { ModuleConfig } from '../../ts-config';
import { isArray, isNotEmptyAny } from '@lhb/func';

export interface TableDetailProps {
  moduleConfig: ModuleConfig;
  jnpObject: Object;
}

const TableDetail: FC<any> = ({
  moduleConfig,
  jnpObject,
  isPdf = false, // 是否pdf展示内容
}) => {
  const { key, title, isYearData, columns } = moduleConfig;
  const [filters, setFilters] = useState<any>(); // 筛选参数
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    if (!isNotEmptyAny(jnpObject)) {
      setTableData([]);
      return;
    }
    const moduleData = jnpObject[key];
    if (!isNotEmptyAny(moduleData) ||
      (isYearData && (!isArray(moduleData?.yearData) || moduleData?.yearData.length < 2)) || (!isYearData && !isNotEmptyAny(moduleData?.data))) {
      setTableData([]);
      return;
    }

    if (isYearData) {
      const { yearData } = moduleData;
      const firstYear = yearData[0] || {};
      const secondYear = yearData[1] || {};

      const tmpData: any[] = moduleConfig.data.map((item: any) => {
        return {
          ...item,
          firstYear: firstYear[item.key],
          secondYear: secondYear[item.key],
        };
      });
      setTableData(tmpData);
    } else {
      const { data: dataOfModuleData } = moduleData;
      const tmpData: any[] = moduleConfig.data.map((row: any) => {
        const tmpRow = {
          ...row,
          dataInRow: row.dataInRow.map((obj: any) => {
            return {
              ...obj,
              value: dataOfModuleData[obj.key] || null
            };
          })
        };
        return tmpRow;
      });
      setTableData(tmpData);
    }
  }, [jnpObject]);

  useEffect(() => {
    setFilters({});
  }, [tableData]);

  const loadData = () => {
    return {
      dataSource: tableData || [],
      count: tableData?.length || 0
    };
  };

  const defaultColumns = columns.map((columnItem: any) => {
    if (isYearData) {
      return {
        ...columnItem,
        ...(isPdf && columnItem.pdfWidth ? { width: columnItem.pdfWidth } : {}), // pdf时设置专用的宽度
        render: (_, record: any) => (
          <YearDataInput
            record={record}
            columnItem={columnItem}
          />),
      };
    } else {
      return {
        ...columnItem,
        ...(isPdf && columnItem.pdfWidth ? { width: columnItem.pdfWidth } : {}), // pdf时设置专用的宽度
        render: (_, record: any) => (
          <SpecialInput
            record={record}
            columnItem={columnItem}
          />),
      };
    }
  });

  return (
    <>
      <V2Title
        type='H2'
        divider
        text={title}
        className='pb-16 pt-16'
      />
      {isPdf ? <V2Table
        filters={filters}
        rowKey={isYearData ? 'name' : 'key'}
        onFetch={loadData}
        defaultColumns={defaultColumns}
        hideColumnPlaceholder
        pagination={false}
        scroll={{ x: '100%' }}
        className={cs(pdfStyles.tableModule, !isYearData && pdfStyles.specialTable, 'mb-16')}
        showHeader={isYearData}
      /> : <V2Table
        filters={filters}
        rowKey={isYearData ? 'name' : 'key'}
        onFetch={loadData}
        defaultColumns={defaultColumns}
        hideColumnPlaceholder
        pagination={false}
        className={cs(styles.tableModule, !isYearData && styles.specialTable, 'mb-16')}
        showHeader={isYearData}
      />}
    </>
  );
};

export default TableDetail;
